import React, { useState, useCallback, useMemo } from 'react';
import {
	CheckSquare,
	Calendar,
	Briefcase,
	Building2,
	User,
	Sparkles,
	FileText,
	Ticket,
	Folder,
	ClipboardList,
	ExternalLink,
	Trash2,
	Users
} from 'lucide-react';
import T from 'Components/Helpers/translate';
import AssistantResource from 'Resources/AssistantChat';
import AgentApprovalsResource from 'Resources/AgentApprovals';
import ProposalCardShell, { type StatusPill } from 'App/shadcn/pages/AIWorkspace/components/ProposalCardShell';
import { openCrmEntity, canOpenCrmEntity, crmEntityLabelKey } from './openCrmEntity';
import FieldGrid from './FieldGrid';
import SegmentAudiencePreview, { isSegmentAudience } from './SegmentAudiencePreview';
import { executionFailureMessage } from './approvalAdapters';
import type { WriteProposal } from './types';

type Props = {
	proposal: WriteProposal;
	onUpdate?: (update: Partial<WriteProposal>) => void;
	defaultExpanded?: boolean;
	/** When true, the card renders in a fully inert state — no approve /
	 *  reject buttons, no field editors fire onChange. Used by the trace
	 *  drawer when displaying an already-resolved approval. */
	readOnly?: boolean;
};

const ENTITY_ICONS: Record<string, React.ComponentType<{ style?: React.CSSProperties }>> = {
	activity: CheckSquare,
	todo: CheckSquare,
	task: CheckSquare,
	appointment: Calendar,
	meeting: Calendar,
	order: Briefcase,
	deal: Briefcase,
	opportunity: Briefcase,
	client: Building2,
	account: Building2,
	company: Building2,
	contact: User,
	person: User,
	lead: Sparkles,
	agreement: FileText,
	ticket: Ticket,
	project: Folder,
	projectplan: ClipboardList,
	segment: Users
};

const ENTITY_LABEL_KEYS: Record<string, string> = {
	activity: 'default.activity',
	todo: 'todo.todo',
	task: 'default.task',
	appointment: 'default.appointment',
	meeting: 'assistant.meeting',
	order: 'default.order',
	deal: 'assistant.deal',
	opportunity: 'default.opportunity',
	client: 'default.client',
	account: 'default.account',
	company: 'default.account',
	contact: 'default.contact',
	person: 'default.contact',
	lead: 'default.lead',
	agreement: 'default.agreement',
	ticket: 'default.ticket',
	project: 'default.project',
	projectplan: 'default.projectPlan',
	segment: 'default.segment'
};

const ACTION_LABEL_KEY: Record<string, string> = {
	create: 'default.create',
	update: 'default.update',
	delete: 'default.delete'
};

const TITLE_KEYS = ['description', 'subject', 'name', 'title'];

// Chip values are raw field values. Collapse ISO date/datetime strings to just
// the calendar date (YYYY-MM-DD) so e.g. an appointment's date/endDate render as
// the day rather than the raw time + timezone offset.
const ISO_DATE = /^\d{4}-\d{2}-\d{2}(?:[T ].*)?$/;
const chipValue = (v: string): string => (ISO_DATE.test(v) ? v.slice(0, 10) : v);

function buildSummary(proposal: WriteProposal): { title: string; chips: string[]; typePill: string | null } {
	const data = proposal.data ?? {};
	let title = '';
	for (const k of TITLE_KEYS) {
		const v = data[k];
		if (typeof v === 'string' && v.trim()) {
			title = v.trim();
			break;
		}
	}
	// For updates/deletes the LLM only sends changed fields + id, so the data
	// rarely carries a recognisable name. Fall back to the display name resolved
	// at draft time so the card reads "Uppdatera Företag · Acme Corp" instead of
	// just "Uppdatera Företag".
	if (!title && proposal.entityDisplayName) {
		title = proposal.entityDisplayName;
	}
	const rawActivityType = data?.activityType;
	const activityTypeName =
		typeof rawActivityType === 'string'
			? rawActivityType
			: typeof rawActivityType === 'object' &&
			  rawActivityType !== null &&
			  typeof (rawActivityType as { name?: unknown }).name === 'string'
			? (rawActivityType as { name: string }).name
			: undefined;
	const subtypeKey = crmEntityLabelKey(proposal.entity, { activityType: activityTypeName });
	const entityKey = subtypeKey ?? ENTITY_LABEL_KEYS[proposal.entity];
	const entityLabel = entityKey ? T(entityKey) : proposal.entity;
	const actionKey = ACTION_LABEL_KEY[proposal.action];
	const actionLabel = actionKey ? T(actionKey) : proposal.action;

	const chips: string[] = [];
	const typePill: string | null = null;
	if (data) {
		for (const [k, v] of Object.entries(data)) {
			if (TITLE_KEYS.includes(k) || k === 'id' || k === 'activityType') continue;
			if (chips.length >= 3) continue;
			if (typeof v === 'string' && v.trim()) chips.push(chipValue(v.trim()));
			else if (typeof v === 'number' || typeof v === 'boolean') chips.push(String(v));
		}
	}

	// If the record carries no date of its own, surface the approval's created date
	// (set only by the company-card section) so every card shows a date.
	if (proposal.createdDate && !chips.some(c => ISO_DATE.test(c))) chips.unshift(proposal.createdDate);

	const head = `${actionLabel} ${entityLabel}`;
	return { title: title ? `${head} · ${title}` : head, chips, typePill };
}

function statusToPill(status: WriteProposal['status'], action: WriteProposal['action']): StatusPill | null {
	switch (status) {
		case 'saving':
			return { label: T('assistant.saving') || 'Saving…', tone: 'saving' };
		case 'saved': {
			const key =
				action === 'delete'
					? 'assistant.actionDeleted'
					: action === 'create'
					? 'assistant.actionCreated'
					: 'assistant.actionUpdated';
			return { label: T(key) || 'Done', tone: 'ok' };
		}
		case 'cancelled':
			return { label: T('assistant.cancelled') || 'Cancelled', tone: 'muted' };
		case 'error':
			return { label: T('default.failed') || 'Failed', tone: 'error' };
		case 'pending':
		default:
			return null;
	}
}

const WriteCard = ({ proposal, onUpdate, defaultExpanded, readOnly = false }: Props) => {
	const [editedData, setEditedData] = useState<Record<string, unknown>>(() => ({ ...proposal.data }));
	const Icon = ENTITY_ICONS[proposal.entity] ?? CheckSquare;

	const readOnlyKeys = useMemo(() => (proposal.action === 'update' ? new Set(['id']) : undefined), [proposal.action]);

	const handleConfirm = useCallback(async () => {
		if (!onUpdate) return;
		onUpdate({ status: 'saving' });
		try {
			if (proposal.approvalId) {
				const modified = { entity: proposal.entity, action: proposal.action, data: editedData };
				const { approval } = await AgentApprovalsResource.approve(proposal.approvalId, undefined, modified);
				// Approve resolves 200 even when the executor itself failed
				// (status='rejected' with resultPayload.success=false). Surface
				// that as a Failed card with the executor's message instead of
				// a misleading green checkmark.
				const failure = executionFailureMessage(
					approval.resultPayload as Record<string, unknown> | null | undefined
				);
				if (failure !== null) {
					onUpdate({
						status: 'error',
						data: editedData,
						error: failure || T('default.failed') || 'Write failed',
						result: approval.resultPayload ?? undefined,
						resultEntityType: approval.resultEntityType ?? null,
						resultEntityId: approval.resultEntityId ?? null
					});
				} else {
					onUpdate({
						status: 'saved',
						data: editedData,
						result: approval.resultPayload ?? undefined,
						resultEntityType: approval.resultEntityType ?? null,
						resultEntityId: approval.resultEntityId ?? null
					});
				}
			} else {
				const result = await AssistantResource.executeWrite(proposal.entity, proposal.action, editedData);
				const failure = executionFailureMessage(result as Record<string, unknown> | null | undefined);
				if (failure !== null) {
					onUpdate({
						status: 'error',
						data: editedData,
						error: failure || T('default.failed') || 'Write failed',
						result
					});
				} else {
					onUpdate({ status: 'saved', result, data: editedData });
				}
			}
		} catch (err) {
			const message = err instanceof Error ? err.message : 'Write failed';
			onUpdate({ status: 'error', error: message });
		}
	}, [proposal.entity, proposal.action, proposal.approvalId, editedData, onUpdate]);

	const handleCancel = useCallback(async () => {
		if (!onUpdate) return;
		if (proposal.approvalId) {
			try {
				await AgentApprovalsResource.reject(proposal.approvalId, 'cancelled_in_chat');
			} catch {
				/* non-fatal */
			}
		}
		onUpdate({ status: 'cancelled' });
	}, [proposal.approvalId, onUpdate]);

	const handleRetry = useCallback(() => {
		onUpdate?.({ status: 'pending', error: undefined });
	}, [onUpdate]);

	// Segment proposals carry the agent's friendly `audience` object — render it
	// as a purpose-built criteria preview instead of a generic JSON-ish badge,
	// and keep it out of the editable field grid (criteria aren't editable here;
	// the user can reject or request changes instead).
	const segmentAudience =
		proposal.entity === 'segment' && isSegmentAudience(editedData.audience) ? editedData.audience : null;
	const gridData = useMemo(
		() =>
			segmentAudience
				? Object.fromEntries(Object.entries(editedData).filter(([k]) => k !== 'audience'))
				: editedData,
		[segmentAudience, editedData]
	);
	// Edits in the grid (e.g. renaming the segment) must not drop the audience —
	// the executor needs it to build the filter blocks on confirm.
	const handleGridChange = useCallback(
		(next: Record<string, unknown>) =>
			setEditedData(segmentAudience ? { ...next, audience: segmentAudience } : next),
		[segmentAudience]
	);

	const summary = useMemo(() => {
		const base = buildSummary({ ...proposal, data: editedData });
		if (typeof proposal.matchingContacts === 'number') {
			base.chips = [
				`≈ ${proposal.matchingContacts.toLocaleString()} ${(
					T('default.contacts') || 'contacts'
				).toLowerCase()}`,
				...base.chips
			];
		}
		if (proposal.status === 'saved' && proposal.result?.id != null) {
			return {
				...base,
				title: `${base.title} · #${String(proposal.result.id)}`,
				chips: [`#${String(proposal.result.id)}`, ...base.chips]
			};
		}
		return base;
	}, [proposal, editedData]);
	const statusPill = statusToPill(proposal.status, proposal.action);
	const isSaving = proposal.status === 'saving';
	const resolved = proposal.status === 'saved' || proposal.status === 'cancelled';
	const errored = proposal.status === 'error';

	const resultEntityType = proposal.resultEntityType ?? proposal.entity;
	const resultEntityId =
		proposal.resultEntityId ??
		(typeof proposal.result?.id === 'number'
			? proposal.result.id
			: typeof proposal.result?.id === 'string'
			? Number(proposal.result.id)
			: null);
	const activityTypeName = useMemo(() => {
		const raw = editedData?.activityType;
		if (raw == null) return undefined;
		if (typeof raw === 'string') return raw;
		if (typeof raw === 'object' && raw && typeof (raw as { name?: unknown }).name === 'string') {
			return (raw as { name: string }).name;
		}
		return undefined;
	}, [editedData]);
	const showViewLink =
		proposal.status === 'saved' &&
		proposal.action !== 'delete' &&
		!!resultEntityType &&
		typeof resultEntityId === 'number' &&
		Number.isFinite(resultEntityId) &&
		resultEntityId > 0 &&
		canOpenCrmEntity(resultEntityType);
	const handleOpenEntity = useCallback(() => {
		if (!showViewLink || !resultEntityType || resultEntityId == null) return;
		openCrmEntity(resultEntityType, resultEntityId, { activityType: activityTypeName });
	}, [showViewLink, resultEntityType, resultEntityId, activityTypeName]);
	const viewEntityLabel = useMemo(() => {
		if (!resultEntityType) return null;
		const key = crmEntityLabelKey(resultEntityType, { activityType: activityTypeName });
		return (key && T(key)) || resultEntityType;
	}, [resultEntityType, activityTypeName]);

	const body = (
		<div>
			{errored && proposal.error && (
				<div
					style={{
						marginBottom: 10,
						padding: '8px 10px',
						borderRadius: 4,
						background: 'rgba(178,52,52,0.08)',
						color: 'var(--ws-err, #b23434)',
						fontSize: 12.5
					}}
				>
					{proposal.error}
				</div>
			)}
			<FieldGrid
				data={gridData}
				onChange={handleGridChange}
				disabled={readOnly || isSaving || resolved}
				readOnlyKeys={readOnlyKeys}
			/>
			{segmentAudience && (
				<SegmentAudiencePreview audience={segmentAudience} matchingContacts={proposal.matchingContacts} />
			)}
		</div>
	);

	const headerAction =
		showViewLink && viewEntityLabel ? (
			<button
				type="button"
				className="ws-btn ws-btn-secondary ws-prop-mini-btn"
				onClick={handleOpenEntity}
				title={T('default.entity.view', { entity: viewEntityLabel })}
			>
				<ExternalLink style={{ width: 12, height: 12 }} />
				<span>{T('default.open')}</span>
			</button>
		) : null;

	return (
		<ProposalCardShell
			icon={proposal.action === 'delete' ? Trash2 : Icon}
			title={summary.title}
			chips={summary.chips}
			typePill={summary.typePill}
			statusPill={statusPill}
			headerAction={headerAction}
			body={body}
			busy={isSaving}
			resolved={resolved}
			approveLabel={errored ? T('assistant.retry') : T(ACTION_LABEL_KEY[proposal.action] ?? 'default.save')}
			rejectLabel={T('assistant.cancel')}
			onApprove={readOnly ? undefined : errored ? handleRetry : resolved ? undefined : handleConfirm}
			onReject={readOnly || resolved || errored ? undefined : handleCancel}
			defaultExpanded={defaultExpanded}
		/>
	);
};

export default WriteCard;
